/**
 * Static structure builder — port of the reference build() keeping every
 * element id (t_/row_/act_/dur_/st_/det_/meta_/note_/dup_/end_/warn_) so the
 * extracted CSS applies unchanged. Inline onclick handlers become data-action
 * attributes handled by one delegated listener in main.ts.
 */
import { esc, fmin } from "@trip/shared/engine/time";
import { isFixed } from "@trip/shared/types";
import { store } from "../store.js";
import { aiKnownEnabled } from "./aisuggest.js";
import { addDayButtonHTML, dayEditControlsHTML, itemEditControlsHTML } from "./editor.js";
import { ico } from "./icons.js";
import { refreshSlot } from "./slots.js";

export function badgeHTML(b: string | undefined): string {
  if (b === "booked") return '<span class="badge b-booked">✓ BOOKED</span>';
  if (b === "book") return '<span class="badge b-book">⚑ BOOK AHEAD</span>';
  if (b === "warn") return '<span class="badge b-warn">⚠ DON’T FORGET</span>';
  if (b === "free") return '<span class="badge b-free">FREE</span>';
  if (b === "walkup") return '<span class="badge b-walkup">NO RESERVATION NEEDED</span>';
  return "";
}

/** Half-hourly target choices from 2 PM to 1 AM. */
export function targetOptions(selected: number): string {
  const out: string[] = [];
  for (let m = 14 * 60; m <= 25 * 60; m += 30) {
    out.push(`<option value="${m}" ${m === selected ? "selected" : ""}>${fmin(m)}</option>`);
  }
  return out.join("");
}

/** "Auto" (0 — use the item's own timing) plus 15-minute steps up to 4h, for
 * how long an open-ended fixed item (passport control, a transfer) actually
 * takes. */
export function fixDurOptions(selected: number): string {
  const label = (m: number) => (m < 60 ? `${m} min` : m % 60 === 0 ? `${m / 60}h` : `${Math.floor(m / 60)}h ${m % 60}m`);
  const out = [`<option value="0" ${selected ? "" : "selected"}>Auto</option>`];
  for (let m = 15; m <= 240; m += 15) out.push(`<option value="${m}" ${m === selected ? "selected" : ""}>${label(m)}</option>`);
  return out.join("");
}

export function buildDayNav() {
  const def = store.def;
  document.getElementById("daynav")!.innerHTML = def.days
    .map(
      (d) => `<a class="daychip${d.trav ? " trav" : ""}" href="#${d.id}">${d.dow} ${d.date}<small>${esc(d.loc)}</small></a>`
    )
    .join("");
}

/** Index of a day's last flexible slot — extra rows mount right after it. */
const lastFlex = (d: { items: unknown[] }) =>
  (d.items as Parameters<typeof isFixed>[0][]).reduce((acc, it, i) => (isFixed(it) ? acc : i), -1);

/**
 * One extra slot row. Same shape and ids as a definition row so every slot
 * behavior (status cycle, lockpop, directions) works unchanged — the key is
 * "x1"… instead of an item index. A row with no state yet renders a picker
 * with a placeholder; choosing something creates the state.
 */
export function extraRowHTML(dayId: string, n: number, opts: string[], slackHint: string): string {
  const def = store.def;
  const key = `x${n}`;
  const hasState = Boolean(store.slots[`${dayId}_${key}`]);
  // ✨ Suggest lives here now — bottom-left, beside "＋ Add an activity" — and
  // carries the day's city inside its label. Blank rows only: a filled extra
  // row is a normal slot. Visibility follows the cached AI-enabled probe
  // (these rows rebuild constantly; renderAll's one-time reveal can't reach
  // rows that don't exist yet).
  // travel-day locs read "A → B"; the city you'd want ideas for is where you land
  const loc = (def.days.find((d) => d.id === dayId)?.loc ?? "").split("→").pop()!.trim();
  const suggestBtn = hasState
    ? ""
    : `<button class="btn-det aibtn" id="sug_${dayId}_${key}" data-action="ai-suggest" data-day="${dayId}"
         style="display:${aiKnownEnabled() && store.role === "edit" ? "" : "none"}" title="Ask for activity ideas for this day">✨ Suggest${loc ? ` for ${esc(loc)}` : ""}</button>`;
  return `
    <div class="row xrow" id="row_${dayId}_${key}">
      <div class="time" id="t_${dayId}_${key}"></div>
      <div class="slot">
        <div class="slotlabel">${ico.plus} Extra time${slackHint ? `<span class="xhint"> — ${esc(slackHint)}</span>` : ""}<span class="dupflag" id="dup_${dayId}_${key}" style="display:none"> · ⚠ SELECTED TWICE</span></div>
        <div class="ctrls">
          <button class="btn-det btn-ico" id="dir_${dayId}_${key}" data-action="directions" data-day="${dayId}" data-i="${key}" style="display:none" title="Turn-by-turn directions to the next stop" aria-label="Directions to the next stop">${ico.compass}</button>
          ${suggestBtn}
          <button class="btn-act" id="act_${dayId}_${key}" data-action="act-pick" data-day="${dayId}" data-i="${key}">
            ${hasState ? "" : "＋ Add an activity"}
          </button>
          <select class="dur" id="dur_${dayId}_${key}" data-action="dur" data-day="${dayId}" data-i="${key}" style="display:none"></select>
          <button class="btn-st" id="st_${dayId}_${key}" data-action="st" data-day="${dayId}" data-i="${key}" style="display:none" title="Cycle: Deciding → Confirmed → Booked">○ Deciding</button>
          <span id="det_${dayId}_${key}"></span>
          <span id="notew_${dayId}_${key}"></span>
        </div>
        <div class="tzwarn" id="tzw_${dayId}_${key}" style="display:none"></div>
        <div class="meta" id="meta_${dayId}_${key}"></div>
        <div class="slotnote print-note" id="note_${dayId}_${key}" style="display:none"></div>
      </div>
    </div>`;
}

export function buildDays() {
  const def = store.def;
  document.getElementById("days")!.innerHTML = addDayFooter(def.days
    .map(
      (d) => `
    <section class="day" id="${d.id}">
      <div class="dayhead">
        <div class="dh-top"><h2>${esc(d.title)}</h2><span style="display:flex; gap:8px; align-items:center;">${
          def.dayHome[d.id]
            ? `<button class="btn-det btn-sm" data-action="daymap" data-day="${d.id}">${ico.route} Route</button>`
            : ""
        }${dayEditControlsHTML(d, def.days.indexOf(d), def.days.length)}<span class="loc">${
          d.trav && def.days.indexOf(d) > 0
            ? `${esc(def.days[def.days.indexOf(d) - 1].loc)} → ${esc(d.loc)}`
            : esc(d.loc)
        }</span></span></div>
        <div class="blurb">${esc(d.blurb)}</div>
      </div>
      <div class="rows">
      ${d.items
        .map((it, i) =>
          isFixed(it)
            ? `
        <div class="row">
          <div class="time" id="t_${d.id}_${i}"></div>
          <div class="fixed-item">${itemEditControlsHTML(d.id, i, d.items.length)}<span class="ic">${it.ic}</span>
            <div class="fi-line"><span class="fl">${esc(it.l)}</span>${badgeHTML(it.badge)}${
              it.blk ? `<button class="blflag" id="blf_${d.id}_${i}" data-action="blflag" data-k="${esc(it.blk)}" style="display:none"></button>` : ""
            }${
              // only indeterminate items (a transfer, passport control, a
              // check-in) take a duration override. Anything timetabled (has
              // an end) is excluded — and so is every flight, even one whose
              // overnight arrival can't be a same-day `e` (KL 612): a flight
              // has a scheduled arrival, not a guessable one.
              !it.e && !(it.ic + it.l).includes("✈")
                ? `<select class="fixdur" id="fixdur_${d.id}_${i}" data-action="fixdur" data-day="${d.id}" data-i="${i}" title="How long this actually takes — the rest of the day shifts to match">${fixDurOptions(store.fixDurs[`${d.id}_${i}`] ?? 0)}</select>`
                : ""
            }${it.det ? `<button class="btn-det btn-sm" data-action="modal" data-id="${it.det}">${ico.info} Details</button>` : ""}${
              // notes are a tooltip at a glance, full text on paper
              it.n ? `<button type="button" class="notechip" data-tip="${esc(it.n)}" aria-label="Note">${ico.note}</button>` : ""
            }${it.n ? `<div class="fn print-note">${esc(it.n)}</div>` : ""}</div>
          </div>
        </div>`
            : `
        <div class="row" id="row_${d.id}_${i}">
          <div class="time" id="t_${d.id}_${i}"></div>
          <div class="slot">
            <div class="slotlabel">${esc(it.s)}${itemEditControlsHTML(d.id, i, d.items.length)}<span class="dupflag" id="dup_${d.id}_${i}" style="display:none"> · ⚠ SELECTED TWICE</span></div>
            <div class="ctrls">
              <button class="btn-det btn-ico" data-action="directions" data-day="${d.id}" data-i="${i}" title="Turn-by-turn directions to the next stop" aria-label="Directions to the next stop">${ico.compass}</button>
              <button class="btn-act" id="act_${d.id}_${i}" data-action="act-pick" data-day="${d.id}" data-i="${i}"></button>
              <select class="dur" id="dur_${d.id}_${i}" data-action="dur" data-day="${d.id}" data-i="${i}"></select>
              <button class="btn-st" id="st_${d.id}_${i}" data-action="st" data-day="${d.id}" data-i="${i}" title="Cycle: Deciding → Confirmed → Booked">○ Deciding</button>
              <span id="det_${d.id}_${i}"></span>
              <span id="notew_${d.id}_${i}"></span>
            </div>
            <div class="tzwarn" id="tzw_${d.id}_${i}" style="display:none"></div>
            <div class="meta" id="meta_${d.id}_${i}"></div>
            <div class="slotnote print-note" id="note_${d.id}_${i}" style="display:none"></div>
          </div>
        </div>${i === lastFlex(d) ? `<div id="xrows_${d.id}"></div>` : ""}`
        )
        .join("")}
      </div>
      <div class="dayend" id="end_${d.id}" style="display:none"></div>
      ${
        def.dayHome[d.id] && !def.noEnd[d.id]
          ? `<div class="daytarget">
               <label for="target_${d.id}">${ico.flag} Back at base by</label>
               <select id="target_${d.id}" data-action="target" data-day="${d.id}">${targetOptions(store.dayEnds[d.id] ?? def.dayEnd)}</select>
               <button class="btn-det" data-action="fit" data-day="${d.id}" title="Add or trim activity time so the day lands as close to the target as possible">${ico.sparkle} Fit to target</button>
             </div>`
          : ""
      }
      <div class="daywarn" id="warn_${d.id}"></div>
    </section>`
    )
    .join(""));

  for (const d of def.days)
    d.items.forEach((it, i) => {
      if (!isFixed(it)) {
        const saved = store.slots[`${d.id}_${i}`];
        if (saved) {
          refreshSlot(d.id, i);
        }
      }
    });
}

/** In edit mode the itinerary ends with the one control that has no anchor day. */
function addDayFooter(daysHtml: string): string {
  return daysHtml + addDayButtonHTML();
}

/** Scrollspy + jump-to-today (date-driven, not the "d"+getDate() hack).
 * Safe to call repeatedly — a mutable definition re-renders, and the previous
 * listeners must come off or they accumulate one set per render. */
let spyListener: (() => void) | null = null;
let jumpedToToday = false;

export function initDayNavSpy() {
  if (spyListener) {
    window.removeEventListener("scroll", spyListener);
    window.removeEventListener("resize", spyListener);
    spyListener = null;
  }
  const def = store.def;
  const nav = document.getElementById("daynav")!;
  const chips = () => nav.querySelectorAll<HTMLElement>(".daychip");
  let last = "";
  function spy() {
    let cur = def.days[0].id;
    const y = window.scrollY + 130;
    def.days.forEach((d) => {
      const sec = document.getElementById(d.id);
      if (sec && sec.offsetTop <= y) cur = d.id;
    });
    if (cur === last) return;
    last = cur;
    chips().forEach((c, i) => c.classList.toggle("cur", def.days[i].id === cur));
    const idx = def.days.findIndex((d) => d.id === cur);
    const chip = chips()[idx];
    if (chip) nav.scrollTo({ left: chip.offsetLeft - nav.clientWidth / 2 + chip.offsetWidth / 2, behavior: "smooth" });
  }
  spyListener = spy;
  window.addEventListener("scroll", spy, { passive: true });
  window.addEventListener("resize", spy);
  spy();

  // mid-trip: scroll to today's section, matching on the day's date text
  const now = new Date();
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const todayLabel = `${monthNames[now.getMonth()]} ${now.getDate()}`;
  const today = def.days.find((d) => d.date === todayLabel);
  if (today && !jumpedToToday) {
    jumpedToToday = true; // only on first render — re-renders shouldn't yank the page
    const el = document.getElementById(today.id);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 400);
  }
}
